import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Handshake, Plus, Shield, Calendar, ArrowRight, Trash2 } from 'lucide-react';

const STATUS_COLORS = {
  open: 'bg-slate-100 text-slate-600', offer_made: 'bg-blue-100 text-blue-700',
  under_discussion: 'bg-amber-100 text-amber-700', agreed: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700', lapsed: 'bg-slate-100 text-slate-500',
  withdrawn: 'bg-slate-100 text-slate-400',
};
const TYPE_LABELS = { offer: 'Offer', counteroffer: 'Counter-Offer', final_offer: 'Final Offer' };

export default function SettlementWorkspace({ matterId }) {
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({
    settlement_type: 'offer', amount_offered: '', time_offered_days: '',
    terms: '', without_prejudice_flag: true, offered_by: '', offered_to: '',
    offered_date: '', expiry_date: '', notes: '',
  });

  const loadData = useCallback(async () => {
    try {
      const res = await api.disputes.listSettlements(matterId);
      setSettlements(res.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!form.terms && !form.amount_offered) { toast.error('Provide amount or terms'); return; }
    try {
      await api.disputes.createSettlement(matterId, {
        ...form, matter_id: matterId,
        amount_offered: form.amount_offered ? parseFloat(form.amount_offered) : null,
        time_offered_days: form.time_offered_days ? parseInt(form.time_offered_days) : null,
      });
      toast.success('Settlement offer created');
      setShowAdd(false);
      setForm({ settlement_type: 'offer', amount_offered: '', time_offered_days: '', terms: '', without_prejudice_flag: true, offered_by: '', offered_to: '', offered_date: '', expiry_date: '', notes: '' });
      loadData();
    } catch { toast.error('Failed to create settlement'); }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await api.disputes.updateSettlement(id, { status });
      toast.success('Settlement updated');
      loadData();
    } catch { toast.error('Update failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  return (
    <div className="space-y-4" data-testid="settlement-workspace">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700">Settlement Negotiations ({settlements.length})</h3>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1 text-xs" onClick={() => setShowAdd(true)} data-testid="add-settlement-btn"><Plus className="h-3 w-3" /> New Offer</Button>
      </div>

      {settlements.length === 0 ? (
        <Card><CardContent className="p-8 text-center"><Handshake className="h-10 w-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No settlement offers yet</p><p className="text-xs text-slate-400 mt-1">Create an offer to begin settlement negotiations</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {settlements.map(s => (
            <Card key={s.id} className={s.without_prejudice_flag ? 'border-l-4 border-l-amber-400' : ''} data-testid={`settlement-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[10px] text-slate-400">{s.settlement_ref}</span>
                      <Badge className={`text-[10px] ${STATUS_COLORS[s.status]}`}>{s.status?.replace(/_/g, ' ')}</Badge>
                      <Badge variant="outline" className="text-[10px]">{TYPE_LABELS[s.settlement_type] || s.settlement_type}</Badge>
                      {s.without_prejudice_flag && <Badge className="bg-amber-100 text-amber-700 text-[8px] gap-0.5"><Shield className="h-2 w-2" /> WP</Badge>}
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {s.amount_offered > 0 && <span className="font-mono font-bold text-slate-800">{new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', maximumFractionDigits: 0 }).format(s.amount_offered)}</span>}
                      {s.time_offered_days > 0 && <span className="text-slate-600">{s.time_offered_days} days</span>}
                    </div>
                    {s.terms && <p className="text-xs text-slate-600 mt-1 line-clamp-2">{s.terms}</p>}
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400">
                      {s.offered_by && <span>From: {s.offered_by}</span>}
                      {s.offered_to && <><ArrowRight className="h-3 w-3" /><span>To: {s.offered_to}</span></>}
                      {s.offered_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{s.offered_date}</span>}
                      {s.expiry_date && <span className="text-red-500">Expires: {s.expiry_date}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {s.status === 'offer_made' && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStatusUpdate(s.id, 'under_discussion')}>Discuss</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs text-green-700" onClick={() => handleStatusUpdate(s.id, 'agreed')}>Accept</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => handleStatusUpdate(s.id, 'rejected')}>Reject</Button>
                      </>
                    )}
                    {s.status === 'under_discussion' && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-xs text-green-700" onClick={() => handleStatusUpdate(s.id, 'agreed')}>Agree</Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs text-red-600" onClick={() => handleStatusUpdate(s.id, 'rejected')}>Reject</Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">New Settlement Offer</DialogTitle>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Offer Type</Label>
              <Select value={form.settlement_type} onValueChange={v => setForm(f => ({...f, settlement_type: v}))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="offer">Offer</SelectItem><SelectItem value="counteroffer">Counter-Offer</SelectItem><SelectItem value="final_offer">Final Offer</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Amount (ZAR)</Label><Input type="number" value={form.amount_offered} onChange={e => setForm(f => ({...f, amount_offered: e.target.value}))} placeholder="0.00" data-testid="settlement-amount-input" /></div>
              <div><Label className="text-xs">Time (days)</Label><Input type="number" value={form.time_offered_days} onChange={e => setForm(f => ({...f, time_offered_days: e.target.value}))} /></div>
            </div>
            <div><Label className="text-xs">Terms</Label><Textarea value={form.terms} onChange={e => setForm(f => ({...f, terms: e.target.value}))} rows={3} placeholder="Settlement terms..." data-testid="settlement-terms-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Offered By</Label><Input value={form.offered_by} onChange={e => setForm(f => ({...f, offered_by: e.target.value}))} placeholder="Party name" /></div>
              <div><Label className="text-xs">Offered To</Label><Input value={form.offered_to} onChange={e => setForm(f => ({...f, offered_to: e.target.value}))} placeholder="Counterparty" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Offer Date</Label><Input type="date" value={form.offered_date} onChange={e => setForm(f => ({...f, offered_date: e.target.value}))} /></div>
              <div><Label className="text-xs">Expiry Date</Label><Input type="date" value={form.expiry_date} onChange={e => setForm(f => ({...f, expiry_date: e.target.value}))} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.without_prejudice_flag} onChange={e => setForm(f => ({...f, without_prejudice_flag: e.target.checked}))} className="rounded" /> Without Prejudice</label>
            <div><Label className="text-xs">Internal Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAdd} data-testid="submit-settlement"><Plus className="h-4 w-4 mr-1" /> Create Offer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
